import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, formatMoney } from "@/lib/api";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { cart, updateQty, removeFromCart, cartTotal, clearCart } = useApp();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (cart.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/cart", {
        items: cart.map((c) => ({ product_id: c.product_id, quantity: c.quantity })),
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="cart-page">
      <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-6">Your Cart</h1>
      {cart.length === 0 ? (
        <div className="card-soft p-8 text-center">
          <p className="text-slate-600">Your cart is empty.</p>
          <Link to="/shop" data-testid="cart-shop-link" className="btn-pink mt-4 inline-block">Shop now</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-3">
            {cart.map((it) => (
              <div key={it.product_id} className="card-soft p-4 flex items-center gap-4" data-testid={`cart-item-${it.product_id}`}>
                <img src={it.image_url} alt="" className="w-20 h-20 rounded-xl object-cover" />
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">{it.name}</div>
                  <div className="text-pink-600 font-bold mt-1">${it.price.toFixed(2)}</div>
                </div>
                <div className="flex items-center bg-white rounded-full ring-1 ring-pink-100 px-2 py-1">
                  <button className="px-2 text-pink-600 font-bold" onClick={() => updateQty(it.product_id, it.quantity - 1)}>−</button>
                  <span className="px-2 font-bold">{it.quantity}</span>
                  <button className="px-2 text-pink-600 font-bold" onClick={() => updateQty(it.product_id, it.quantity + 1)}>+</button>
                </div>
                <button data-testid={`remove-${it.product_id}`} className="text-slate-500 hover:text-red-600" onClick={() => removeFromCart(it.product_id)}>
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>
          <div>
            <div className="card-soft p-6 sticky top-24">
              <h3 className="font-display font-bold text-xl mb-4">Summary</h3>
              <div className="flex justify-between text-sm mb-2"><span>Subtotal</span><span data-testid="cart-subtotal">{formatMoney(cartTotal)}</span></div>
              <div className="flex justify-between text-sm mb-2"><span>Shipping</span><span>Calculated at checkout</span></div>
              <div className="border-t border-pink-100 my-3" />
              <div className="flex justify-between text-lg font-bold"><span>Total</span><span data-testid="cart-total" className="text-pink-600">{formatMoney(cartTotal)}</span></div>
              <button data-testid="checkout-btn" disabled={loading} onClick={checkout} className="btn-pink w-full mt-5">
                {loading ? "Redirecting..." : "Secure Checkout"}
              </button>
              <button data-testid="clear-cart-btn" onClick={clearCart} className="w-full mt-2 text-sm text-slate-500 hover:text-slate-700">Clear cart</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
